import React, { useState, useEffect, useRef } from "react";
import { X, FileWarning, Check, FileText, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * Modale di accettazione disclaimer prima del download di un documento riservato.
 *
 * Comportamento richiesto dall'admin:
 *   - Ogni volta che l'utente NON-admin clicca "Scarica" deve apparire questa modale.
 *   - 3 bottoni:
 *       Accetto → registra audit firmato + richiama onAccept (procede con il download)
 *       Rifiuto → chiude la modale (nessun download, nessun audit)
 *       Disclaimer → mostra il testo legale completo (5 punti)
 *
 * UX:
 *   - Focus iniziale sul bottone "Accetto" (azione primaria positiva).
 *   - ESC chiude come "Rifiuto" (default safe).
 *   - Il testo legale è espandibile inline (no terza modale annidata).
 *
 * Audit:
 *   - Prima di chiamare onAccept, fa una POST a /api/downloads/audit/accept con
 *     l'asset_id. Il backend salva timestamp/IP/user-agent/disclaimer_version
 *     + HMAC-SHA256 signature. Se la chiamata fallisce, AVVISA l'utente ma NON
 *     blocca il download (il download è il bene legale per l'utente — l'audit
 *     server-side è il "best effort" che protegge il gestore).
 */
export default function DownloadDisclaimerModal({ open, onAccept, onClose, fileLabel, assetId, customAccept }) {
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const acceptRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setShowDisclaimer(false);
      setAccepting(false);
      return;
    }
    // Focus pulsante "Accetto" per accessibilità + permette Enter
    setTimeout(() => acceptRef.current?.focus(), 50);
    const onKey = (e) => {
      if (e.key === "Escape" && !accepting) onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, accepting]);

  async function handleAccept() {
    if (accepting) return;
    setAccepting(true);
    // Flusso "guest" (link condiviso pubblico): l'accettazione DEVE riuscire
    // perché è il gate legale che sblocca il download. Se fallisce, NON procede.
    if (customAccept) {
      try {
        await customAccept();
      } catch {
        setAccepting(false);
        return;
      }
      setAccepting(false);
      onAccept?.();
      return;
    }
    try {
      if (assetId) {
        // Registra accettazione firmata. Errori sono best-effort: loggati ma
        // non bloccanti per il download.
        await api.post("/downloads/audit/accept", { asset_id: assetId });
      }
    } catch (err) {
      // Audit fallito → permettiamo comunque il download per non frustrare
      // l'utente legittimo. Il backend ha comunque l'activity_log fallback.
      console.warn("[audit] accept registration failed:", err?.response?.data || err);
    } finally {
      setAccepting(false);
      onAccept?.();
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-ink/60 backdrop-blur-sm"
      data-testid="download-disclaimer-modal"
      onClick={(e) => {
        // Click su backdrop = rifiuto (più conservativo di accettare implicitamente)
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="bg-cream w-[min(96vw,640px)] max-h-[90vh] overflow-hidden flex flex-col border border-ink/15 shadow-2xl rounded-sm"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-ink/10 flex items-start justify-between gap-3 bg-white">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 shrink-0 rounded-sm bg-gold/15 text-gold flex items-center justify-center">
              <FileWarning size={18} />
            </div>
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase text-slate1">
                Documento riservato
              </div>
              <div className="text-base font-light text-ink mt-0.5">
                Conferma download
              </div>
              {fileLabel && (
                <div className="text-[11px] text-slate1 mt-0.5 max-w-[420px] truncate" title={fileLabel}>
                  {fileLabel}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => onClose?.()}
            className="text-slate1 hover:text-ink p-1 rounded-sm hover:bg-ink/5 transition-colors"
            aria-label="Chiudi"
            data-testid="disclaimer-close-x"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 overflow-y-auto flex-1">
          <p className="text-[15px] leading-relaxed text-ink">
            {`Ho letto e accetto le `}<strong>Condizioni di Utilizzo</strong>{` e il `}
            <strong>Divieto di Divulgazione</strong>{` del materiale scaricabile.`}
          </p>

          {showDisclaimer && (
            <div
              className="mt-4 bg-white border border-ink/10 rounded-sm p-4 text-[12.5px] leading-relaxed text-ink/90 max-h-[40vh] overflow-y-auto space-y-3"
              data-testid="disclaimer-full-text"
            >
              <p>
                {`Accedendo, visualizzando o scaricando i documenti, i manuali, le schede tecniche e tutto il materiale informativo (di seguito definito genericamente "Materiale") presente su questo sito, l'utente accetta integralmente e senza riserve le seguenti condizioni:`}
              </p>
              <div>
                <div className="font-medium text-ink mt-2">1. Provenienza e Legittimità dei Contenuti</div>
                <p>
                  {`Il Materiale messo a disposizione su questo portale proviene da fornitori diretti del gestore del sito, da aziende terze collaboratrici, o è stato reperito da fonti per le quali non risulta alcun esplicito divieto di utilizzo a scopo consultativo. Il gestore del sito dichiara di aver acquisito e reso disponibile tale Materiale in buona fede e, per quanto a sua conoscenza, da soggetti autorizzati alla sua condivisione. I diritti di proprietà intellettuale sui documenti restano in capo ai rispettivi autori, produttori e/o fornitori originali.`}
                </p>
              </div>
              <div>
                <div className="font-medium text-ink mt-2">2. Uso Esclusivo, Riservato e Consultativo</div>
                <p>
                  {`Tutto il Materiale è fornito esclusivamente a scopo consultativo e informativo. L'utente è autorizzato a visualizzare e scaricare i documenti per il solo uso personale o strettamente interno alla propria attività. L'accesso a questi contenuti non conferisce all'utente alcun diritto di proprietà, licenza di sfruttamento commerciale o diritto d'autore sui documenti scaricati.`}
                </p>
              </div>
              <div>
                <div className="font-medium text-ink mt-2">3. Divieto Assoluto di Divulgazione e Riproduzione</div>
                <p>
                  {`Il Materiale è strettamente riservato. È severamente vietata qualsiasi forma di riproduzione, copia, pubblicazione, trasmissione, distribuzione, condivisione o divulgazione (sia a titolo gratuito che oneroso) del Materiale a terzi. L'utente si impegna formalmente a non diffondere i documenti su altri siti web, forum, social network, chat o attraverso alcun mezzo di comunicazione, nonché a non utilizzarli per scopi concorrenziali. Qualsiasi utilizzo difforme dalla mera consultazione privata costituisce una grave violazione dei presenti termini.`}
                </p>
              </div>
              <div>
                <div className="font-medium text-ink mt-2">4. Esonero e Limitazione di Responsabilità</div>
                <p>
                  {`Pur prestando la massima cura nel reperimento e nella messa a disposizione della documentazione, il gestore di questo sito opera come mero intermediario tecnico e informativo. Di conseguenza:`}
                </p>
                <ul className="list-disc list-inside pl-2 mt-1 space-y-1">
                  <li>{`Non garantisce l'assoluta esattezza, completezza o il costante aggiornamento del Materiale fornito da terzi.`}</li>
                  <li>{`Non si assume alcuna responsabilità per eventuali danni, diretti o indiretti, derivanti dall'utilizzo, dall'applicazione o dall'affidamento sui dati contenuti nei documenti.`}</li>
                  <li>{`L'utente si assume la piena e totale responsabilità per qualsiasi uso illecito o improprio del Materiale.`}</li>
                </ul>
              </div>
              <div>
                <div className="font-medium text-ink mt-2">5. Manleva</div>
                <p>
                  {`In caso di violazione delle presenti disposizioni (in particolare per quanto riguarda il divieto di divulgazione), l'utente si impegna a manlevare e tenere indenne il gestore del sito da qualsiasi pretesa, richiesta di risarcimento danni, sanzione o azione legale intrapresa dai legittimi proprietari dei documenti o da terze parti.`}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer · 3 bottoni */}
        <div className="px-6 py-4 border-t border-ink/10 bg-white flex flex-wrap items-center justify-between gap-3">
          <button
            onClick={() => setShowDisclaimer((v) => !v)}
            className="inline-flex items-center gap-2 px-4 py-2 border border-ink/15 text-[11px] tracking-[0.2em] uppercase text-ink hover:bg-ink/5 rounded-sm transition-colors"
            data-testid="disclaimer-toggle-btn"
          >
            <FileText size={13} />
            {showDisclaimer ? "Nascondi Disclaimer" : "Disclaimer"}
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <button
              onClick={() => onClose?.()}
              disabled={accepting}
              className="px-5 py-2 border border-ink/15 text-[11px] tracking-[0.2em] uppercase text-slate1 hover:text-ink hover:bg-ink/5 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="disclaimer-reject-btn"
            >
              Rifiuto
            </button>
            <button
              ref={acceptRef}
              onClick={handleAccept}
              disabled={accepting}
              className="inline-flex items-center gap-2 px-5 py-2 bg-ink text-cream text-[11px] tracking-[0.2em] uppercase hover:bg-ink/90 rounded-sm transition-colors disabled:opacity-70 disabled:cursor-wait"
              data-testid="disclaimer-accept-btn"
            >
              {accepting ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              {accepting ? "Registrazione..." : "Accetto"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
